import { useState, useEffect, useCallback } from 'react';
import { eventsService } from '../lib/database';
import { getDataSource } from '../lib/config';
import type { Event } from '../types';

interface UseEventsReturn {
  events: Event[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  create: (event: Omit<Event, 'id' | 'createdAt' | 'updatedAt'>) => Promise<Event>;
  update: (id: string | number, updates: Partial<Event>) => Promise<Event>;
  remove: (id: string | number) => Promise<void>;
  getByDate: (date: string) => Promise<Event[]>;
}

export const useEvents = (enabled = true): UseEventsReturn => {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchEvents = useCallback(async () => {
    const dataSource = getDataSource();

    if (dataSource === 'mock') {
      setEvents([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await eventsService.getAll();
      setEvents(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load events';
      setError(errorMessage);
      console.error('Error fetching events:', err);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    fetchEvents();
  }, [fetchEvents, enabled]);

  const create = useCallback(
    async (event: Omit<Event, 'id' | 'createdAt' | 'updatedAt'>): Promise<Event> => {
      const dataSource = getDataSource();

      if (dataSource === 'mock') {
        const newEvent: Event = {
          ...event,
          id: `mock-${Date.now()}`,
          createdAt: Date.now(),
          updatedAt: Date.now(),
        };
        setEvents((prev) => [...prev, newEvent]);
        return newEvent;
      }

      setError(null);
      try {
        const newEvent = await eventsService.create(event);
        setEvents((prev) => [...prev, newEvent]);
        return newEvent;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to create event';
        setError(errorMessage);
        throw err;
      }
    },
    []
  );

  const update = useCallback(
    async (id: string | number, updates: Partial<Event>): Promise<Event> => {
      const dataSource = getDataSource();

      if (dataSource === 'mock') {
        const updatedEvent = events.find((e) => e.id === id);
        if (!updatedEvent) throw new Error('Event not found');
        const newEvent = { ...updatedEvent, ...updates, updatedAt: Date.now() };
        setEvents((prev) => prev.map((e) => (e.id === id ? newEvent : e)));
        return newEvent;
      }

      setError(null);
      try {
        const updatedEvent = await eventsService.update(id, updates);
        setEvents((prev) => prev.map((e) => (e.id === id ? updatedEvent : e)));
        return updatedEvent;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to update event';
        setError(errorMessage);
        throw err;
      }
    },
    [events]
  );

  const remove = useCallback(async (id: string | number): Promise<void> => {
    const dataSource = getDataSource();

    if (dataSource === 'mock') {
      setEvents((prev) => prev.filter((e) => e.id !== id));
      return;
    }

    setError(null);
    try {
      await eventsService.delete(id);
      setEvents((prev) => prev.filter((e) => e.id !== id));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete event';
      setError(errorMessage);
      throw err;
    }
  }, []);

  const getByDate = useCallback(
    async (date: string): Promise<Event[]> => {
      const dataSource = getDataSource();

      if (dataSource === 'mock') {
        return events.filter((e) => e.eventDate === date);
      }

      try {
        return await eventsService.getByDate(date);
      } catch (err) {
        console.error('Error fetching events by date:', err);
        return [];
      }
    },
    [events]
  );

  return {
    events,
    loading,
    error,
    refresh: fetchEvents,
    create,
    update,
    remove,
    getByDate,
  };
};
